import {
  getTransportLabel,
  getTransportVisualConfig,
  hasValidLatLng
} from './mapUtils';
import { resolveTransportIdentity } from './transportIdentity';
import { OFFICE_LOCATION, getRouteStatus } from './LogisticsUtils';

export const LIVE_MONITOR_COLOR_PALETTE = [
  { color: '#2563eb', softColor: '#dbeafe' },
  { color: '#dc2626', softColor: '#fee2e2' },
  { color: '#16a34a', softColor: '#dcfce7' },
  { color: '#7c3aed', softColor: '#ede9fe' },
  { color: '#ea580c', softColor: '#ffedd5' },
  { color: '#0f766e', softColor: '#ccfbf1' },
  { color: '#c026d3', softColor: '#f5d0fe' },
  { color: '#0891b2', softColor: '#cffafe' },
  { color: '#ca8a04', softColor: '#fef3c7' },
  { color: '#be123c', softColor: '#ffe4e6' }
];

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const hexToRgb = (hex) => {
  const normalized = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return { r: 15, g: 23, b: 42 };

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const mixHexColor = (hex, targetHex, amount = 0.5) => {
  const source = hexToRgb(hex);
  const target = hexToRgb(targetHex);
  const ratio = Math.max(0, Math.min(1, amount));

  return `rgb(${clampChannel(source.r + (target.r - source.r) * ratio)}, ${clampChannel(source.g + (target.g - source.g) * ratio)}, ${clampChannel(source.b + (target.b - source.b) * ratio)})`;
};

export const withAlpha = (hex, alpha = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getTransportSpeedKmh = (transportType = 'camioneta') => {
  const normalized = String(transportType || 'camioneta').toLowerCase();
  const speeds = {
    moto: 32,
    camioneta: 38,
    auto: 42,
    camion: 30
  };

  return speeds[normalized] || 35;
};

export const getRelativeTimeLabel = (timestampMillis) => {
  if (!timestampMillis) return 'Sin datos';

  const diffMinutes = Math.max(0, Math.round((Date.now() - timestampMillis) / 60000));

  if (diffMinutes <= 1) return 'Hace instantes';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  if (hours < 24) return `Hace ${hours}h ${minutes}m`;

  return new Date(timestampMillis).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeLivePath = (path = []) =>
  (Array.isArray(path) ? path : []).filter((point) => hasValidLatLng(point));

const getPendingDestinations = (route) =>
  [...(route?.destinations || [])]
    .filter((dest) => dest.status === 'pending')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

const getLastCompletedDestination = (route) =>
  [...(route?.destinations || [])]
    .filter((dest) => ['completed', 'incomplete'].includes(dest.status) && hasValidLatLng(dest.latlng))
    .sort((a, b) => getTimestampMillis(b.completedAt) - getTimestampMillis(a.completedAt))[0] || null;

const getNextLiveDestination = (route) => {
  const activeDestination = (route?.destinations || []).find((dest) => dest.status === 'en_camino');
  if (activeDestination) return activeDestination;

  const atDestination = (route?.destinations || []).find((dest) => dest.status === 'en_destino');
  if (atDestination) return atDestination;

  return getPendingDestinations(route)[0] || null;
};

const getObservedSpeedKmh = (transporterPath = []) => {
  const validPath = normalizeLivePath(transporterPath);
  if (validPath.length < 2) return 0;

  const lastPoint = validPath[validPath.length - 1];
  const previousPoint = validPath[validPath.length - 2];

  const lastTimestamp = getTimestampMillis(lastPoint?.timestamp);
  const previousTimestamp = getTimestampMillis(previousPoint?.timestamp);

  if (!lastTimestamp || !previousTimestamp || lastTimestamp <= previousTimestamp) return 0;

  const distanceKm = calculateDistance(
    Number(previousPoint.lat),
    Number(previousPoint.lng),
    Number(lastPoint.lat),
    Number(lastPoint.lng)
  );
  const hours = (lastTimestamp - previousTimestamp) / 3600000;

  if (!hours || hours <= 0) return 0;

  return distanceKm / hours;
};

const formatEtaLabel = (etaMinutes) => {
  if (!Number.isFinite(etaMinutes) || etaMinutes < 0) return 'N/D';
  if (etaMinutes < 1) return '< 1 min';
  if (etaMinutes < 60) return `${Math.round(etaMinutes)} min`;

  const hours = Math.floor(etaMinutes / 60);
  const minutes = Math.round(etaMinutes % 60);
  return `${hours}h ${minutes}m`;
};

const buildLiveStatus = ({ currentLocation, nextDestination, observedSpeedKmh, lastUpdateMs, etaMinutes }) => {
  if (!currentLocation) {
    return {
      key: 'no_position',
      label: 'Sin posición',
      color: '#64748b',
      softColor: '#f1f5f9'
    };
  }

  const minutesSinceUpdate = lastUpdateMs
    ? (Date.now() - lastUpdateMs) / 60000
    : Number.POSITIVE_INFINITY;

  if (minutesSinceUpdate > 20) {
    return {
      key: 'stale',
      label: 'Sin señal reciente',
      color: '#ef4444',
      softColor: '#fef2f2'
    };
  }

  if (nextDestination?.status === 'en_destino') {
    return {
      key: 'at_destination',
      label: 'En destino',
      color: '#0ea5e9',
      softColor: '#ecfeff'
    };
  }

  if (Number.isFinite(etaMinutes) && etaMinutes <= 10) {
    return {
      key: 'arriving',
      label: 'Próximo arribo',
      color: '#f59e0b',
      softColor: '#fffbeb'
    };
  }

  if (observedSpeedKmh >= 8) {
    return {
      key: 'moving',
      label: 'En movimiento',
      color: '#16a34a',
      softColor: '#f0fdf4'
    };
  }

  return {
    key: 'stopped',
    label: 'Detenido',
    color: '#8b5cf6',
    softColor: '#f5f3ff'
  };
};

const getStableHash = (value) => {
  const normalized = String(value || 'default');
  let hash = 0;

  for (let i = 0; i < normalized.length; i += 1) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
    hash |= 0;
  }

  return Math.abs(hash);
};

const buildSoftColorFromHex = (hex) => mixHexColor(hex, '#ffffff', 0.82);

const resolveMonitorIdentityColors = (route, fallbackVisual) => {
  const resolvedIdentity = resolveTransportIdentity(route);

  return {
    color: resolvedIdentity.identityColor || fallbackVisual.color,
    softColor: resolvedIdentity.identitySoftColor || fallbackVisual.softColor
  };
};

const isWeakTransportName = (value, transportKey, transportLabel) => {
  const normalized = normalizeText(value);
  const normalizedTransportKey = normalizeText(transportKey);
  const normalizedTransportLabel = normalizeText(transportLabel);

  if (!normalized) return true;

  const weakValues = new Set([
    'moto',
    'camioneta',
    'auto',
    'camion',
    'camión',
    'vehiculo',
    'vehículo',
    'transporte',
    'unidad',
    'sin asignar',
    'chofer sin asignar',
    `transporte ${normalizedTransportKey}`,
    `transporte ${normalizedTransportLabel}`
  ]);

  return weakValues.has(normalized) || normalized === normalizedTransportKey || normalized === normalizedTransportLabel;
};

const resolveTransporterDisplayName = (route, transportKey, transportLabel) => {
  const candidates = [
    route?.choferName,
    route?.driverName,
    route?.assignedDriverName,
    route?.assignedToName,
    route?.transporterPersonName,
    route?.transporterFullName,
    route?.driver?.name,
    route?.driver?.fullName,
    route?.chofer?.name,
    route?.chofer?.fullName,
    route?.assignedTo?.name,
    route?.assignedDriver?.name,
    route?.transporter?.personName,
    route?.transporter?.driverName,
    route?.transporter?.fullName,
    route?.transporterName,
    route?.transporter?.name
  ];

  for (const candidate of candidates) {
    const trimmed = String(candidate || '').trim();
    if (!trimmed) continue;
    if (isWeakTransportName(trimmed, transportKey, transportLabel)) continue;
    return trimmed;
  }

  return 'Chofer sin asignar';
};

export const buildLiveRouteSnapshot = (route, officeLocation = OFFICE_LOCATION) => {
  const normalizedRouteStatus = getRouteStatus(route);
  const transportKey = route?.transport || route?.transportType || 'camioneta';
  const transportLabel = getTransportLabel(transportKey);
  const transporterPath = normalizeLivePath(route?.transporterLivePath || []);
  const persistedCurrentLocation = hasValidLatLng(route?.transporterLastLocation)
    ? {
        lat: Number(route.transporterLastLocation.lat),
        lng: Number(route.transporterLastLocation.lng),
        timestamp: route.transporterLastLocation.timestamp || null
      }
    : transporterPath[transporterPath.length - 1] || null;

  const shouldUseOfficeAsProvisionalLocation =
    !persistedCurrentLocation &&
    hasValidLatLng(officeLocation) &&
    ['in_progress', 'pending', 'programmed'].includes(normalizedRouteStatus);

  const currentLocation = persistedCurrentLocation ||
    (shouldUseOfficeAsProvisionalLocation
      ? {
          lat: Number(officeLocation.lat),
          lng: Number(officeLocation.lng),
          timestamp: null,
          isProvisional: true
        }
      : null);

  const nextDestination = getNextLiveDestination(route);
  const lastCompletedDestination = getLastCompletedDestination(route);
  const totalDestinations = Array.isArray(route?.destinations) ? route.destinations.length : 0;
  const completedDestinations = (route?.destinations || []).filter((dest) =>
    ['completed', 'incomplete', 'cancelled'].includes(dest.status)
  ).length;
  const progress = totalDestinations > 0 ? (completedDestinations / totalDestinations) * 100 : 0;

  const observedSpeedKmh = getObservedSpeedKmh(transporterPath);
  const fallbackSpeed = getTransportSpeedKmh(transportKey);
  const effectiveSpeed = observedSpeedKmh >= 8 && observedSpeedKmh <= 95 ? observedSpeedKmh : fallbackSpeed;

  let distanceToNextKm = null;
  if (currentLocation && hasValidLatLng(nextDestination?.latlng)) {
    distanceToNextKm = calculateDistance(
      Number(currentLocation.lat),
      Number(currentLocation.lng),
      Number(nextDestination.latlng.lat),
      Number(nextDestination.latlng.lng)
    );
  }

  const etaMinutes =
    Number.isFinite(distanceToNextKm) && effectiveSpeed > 0
      ? (distanceToNextKm / effectiveSpeed) * 60
      : null;

  const lastUpdateMs = getTimestampMillis(currentLocation?.timestamp) || getTimestampMillis(route?.updatedAt);
  const liveStatus = buildLiveStatus({
    currentLocation,
    nextDestination,
    observedSpeedKmh,
    lastUpdateMs,
    etaMinutes
  });

  const transportVisual = getTransportVisualConfig(transportKey);
  const identityColors = resolveMonitorIdentityColors(route, transportVisual);

  const gpsAgeMinutes = lastUpdateMs ? (Date.now() - lastUpdateMs) / 60000 : Number.POSITIVE_INFINITY;
  const gpsActive = Boolean(
    persistedCurrentLocation &&
    !currentLocation?.isProvisional &&
    gpsAgeMinutes <= 10
  );

  return {
    id: route.id,
    route: { ...route, status: normalizedRouteStatus },
    name: route.name || 'Ruta activa',
    transporterName: resolveTransporterDisplayName(route, transportKey, transportLabel),
    transportKey,
    transportLabel,
    transportVisual,
    monitorAccentColor: identityColors.color,
    monitorAccentSoftColor: identityColors.softColor,
    totalDestinations,
    completedDestinations,
    progress,
    currentLocation,
    transporterPath,
    nextDestination,
    lastCompletedDestination,
    activeLeg: nextDestination
      ? {
          from: lastCompletedDestination
            ? { ...lastCompletedDestination, latlng: lastCompletedDestination.latlng }
            : { latlng: officeLocation, name: officeLocation?.name || OFFICE_LOCATION.name },
          to: nextDestination
        }
      : null,
    distanceToNextKm,
    observedSpeedKmh,
    etaMinutes,
    etaLabel: formatEtaLabel(etaMinutes),
    lastUpdateMs,
    lastUpdateLabel: getRelativeTimeLabel(lastUpdateMs),
    liveStatusKey: liveStatus.key,
    liveStatusLabel: liveStatus.label,
    statusColor: liveStatus.color,
    statusSoftColor: liveStatus.softColor,
    destinations: route.destinations || [],
    gpsActive
  };
};

export const getLiveRoutesSnapshot = (activeRoutes = [], officeLocation = OFFICE_LOCATION) => {
  return (activeRoutes || [])
    .map((route) => buildLiveRouteSnapshot(route, officeLocation))
    .sort((a, b) => {
      if (a.liveStatusKey === 'moving' && b.liveStatusKey !== 'moving') return -1;
      if (a.liveStatusKey !== 'moving' && b.liveStatusKey === 'moving') return 1;
      return (a.etaMinutes || Number.POSITIVE_INFINITY) - (b.etaMinutes || Number.POSITIVE_INFINITY);
    });
};
